import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Header } from 'components/home';
import { TopNavBar } from 'components/top_nav_bar';
import { ContainerForm } from 'components/shared/container_form';
import { MenuProvider } from 'contexts/menu/menu_context';
import { useNavigate, useParams } from 'react-router-dom';
import { CuffaAPI } from 'api/cuffa/cuffa.api';
import { CuffaCommercializationChannelEnum } from 'interfaces/cuffa.interface';
import { ROUTES } from 'routes/constants';

interface ICanaisComercializacaoData {
  canaisSelecionados: string[];
  ordens: { value: string; order: number }[];
  outroTexto: string;
}

// 1. ADICIONADO: Mapa para traduzir as chaves do backend para os valores do frontend
const backendKeyToChannelMap: { [key: string]: CuffaCommercializationChannelEnum } = {
  'cooperativas_agricultura_familiar': CuffaCommercializationChannelEnum.COOPERATIVAS_AGRICULTURA_FAMILIAR,
  'agroindustrias_agricultura_familiar': CuffaCommercializationChannelEnum.AGROINDUSTRIAS_AGRICULTURA_FAMILIAR,
  'cooperativas_nao_agricultura_familiar': CuffaCommercializationChannelEnum.COOPERATIVAS_NAO_AGRICULTURA_FAMILIAR,
  'agroindustrias_nao_agricultura_familiar': CuffaCommercializationChannelEnum.AGROINDUSTRIAS_NAO_AGRICULTURA_FAMILIAR,
  'bares_restaurantes_hoteis': CuffaCommercializationChannelEnum.BARES_RESTAURANTES_LANCHONETES_HOTEIS,
  'exportacao_empresas_exportadoras': CuffaCommercializationChannelEnum.EXPORTACAO_EMPRESAS_EXPORTADORAS,
  'feiras': CuffaCommercializationChannelEnum.FEIRAS,
  'mercados_outros_estados': CuffaCommercializationChannelEnum.MERCADOS_SUPERMERCADOS_OUTROS_ESTADOS,
  'mercados_outros_municipios_mt': CuffaCommercializationChannelEnum.MERCADOS_SUPERMERCADOS_OUTROS_MUNICIPIOS_MT,
  'mercados_municipio': CuffaCommercializationChannelEnum.MERCADOS_SUPERMERCADOS_QUITANDAS_MUNICIPIO,
  'programa_aquisicao_alimentos': CuffaCommercializationChannelEnum.PROGRAMA_AQUISICAO_ALIMENTOS_PAA,
  'programa_alimentacao_escolar': CuffaCommercializationChannelEnum.PROGRAMA_NACIONAL_ALIMENTACAO_ESCOLAR_PNAE,
  'revendedores_atravessadores': CuffaCommercializationChannelEnum.REVENDEDORES_ATRAVESSADORES_INTERMEDIARIOS,
  'venda_direta_consumidor': CuffaCommercializationChannelEnum.VENDA_DIRETA_CONSUMIDOR_FINAL,
  'vendas_orgaos_publicos': CuffaCommercializationChannelEnum.VENDAS_ORGAOS_PUBLICOS_COMPRAS_GOVERNAMENTAIS,
  'outros': CuffaCommercializationChannelEnum.OUTRO,
};


export const CanaisComercializacaoVisualizar: React.FC = () => {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();

  const [dados, setDados] = useState<ICanaisComercializacaoData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchChannelsData = async () => {
      if (!uuid) {
        setLoading(false);
        return;
      }
      // Primeiro: tentar restaurar do localStorage (específico por uuid, depois global)
      try {
        const storageKeys: string[] = [];
        if (uuid) storageKeys.push(`cuffa-storage-${uuid}`);
        storageKeys.push('cuffa-storage');

        for (const storageKey of storageKeys) {
          try {
            const raw = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;
            if (!raw) continue;
            const parsed = JSON.parse(raw);
            const saved = parsed?.state?.dadosParciais?.canaisComercializacao;
            if (!saved) continue;

            const canaisSelecionados: string[] = Array.isArray(saved.canaisSelecionados) ? saved.canaisSelecionados : [];
            const ordens: { value: string; order: number }[] = Array.isArray(saved.ordens) ? saved.ordens : [];
            const outroTexto: string = typeof saved.outroTexto === 'string' ? saved.outroTexto : '';

            const mappedData: ICanaisComercializacaoData = { canaisSelecionados, ordens, outroTexto };
            setDados(mappedData);
            setLoading(false);
            return;
          } catch (e) {
            console.warn(`Falha ao parsear ${storageKey} ao restaurar canaisComercializacao:`, e);
            continue;
          }
        }
      } catch (e) {
        console.warn('Erro ao acessar localStorage cuffa-storage ao restaurar canaisComercializacao', e);
      }

      // Se não encontrou no storage, buscar pela API
      try {
        const response = await CuffaAPI.getDataByID(uuid);

        if (response && response.dados) {
          const apiData = response.dados;
          const channelsAPI = apiData.commercialization_channels || {};

          const canaisSelecionados: string[] = [];
          const ordens: { value: string; order: number }[] = [];
          let outroTexto = '';

          for (const [backendKey, data] of Object.entries(channelsAPI)) {
            const frontendValue = backendKeyToChannelMap[backendKey];
            if (!frontendValue) continue;

            let priority: number | undefined;
            if (frontendValue === CuffaCommercializationChannelEnum.OUTRO && Array.isArray(data) && data.length > 0) {
              priority = data[0]?.priority;
              outroTexto = data[0]?.description || '';
            } else if (typeof data === 'number') {
              priority = data;
            }

            if (priority) {
              canaisSelecionados.push(frontendValue);
              ordens.push({ value: frontendValue, order: priority });
            }
          }

          const mappedData: ICanaisComercializacaoData = { canaisSelecionados, ordens, outroTexto };
          setDados(mappedData);
        } else {
          console.error("Dados de canais não encontrados na resposta da API:", response);
          setDados(null);
        }
      } catch (error) {
        console.error("Erro ao buscar dados de canais:", error);
        setDados(null);
      } finally {
        setLoading(false);
      }
    };

    fetchChannelsData();
  }, [uuid]);

  // 4. Adicionados blocos para exibir o status de 'Carregando' e 'Erro'
  if (loading) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Canais de Comercialização" showBackButton />
        <Box width="100%" display="flex" justifyContent="center" alignItems="center" height="50vh">
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Carregando dados...</Typography>
        </Box>
      </MenuProvider>
    );
  }

  if (!dados) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Canais de Comercialização" showBackButton />
        <Box width="100%" display="flex" justifyContent="center" margin="2rem 0">
          <Typography>Não foi possível carregar os dados. Verifique o console para mais detalhes.</Typography>
        </Box>
      </MenuProvider>
    );
  }

  // O restante do código JSX, incluindo a ordenação, continua o mesmo
  const canaisOrdenados = [...dados.ordens]
    .filter(item => dados.canaisSelecionados.includes(item.value))
    .sort((a, b) => a.order - b.order);

  return (
    <MenuProvider>
      <TopNavBar />
      <Header title="Visualização dos Canais de Comercialização" showBackButton />
      <Box width="100%" display="flex" justifyContent="center" margin="2rem">
        <Box sx={{ maxWidth: '1152px', padding: '2rem', width: '100%' }}>
          <ContainerForm title="Canais utilizados na comercialização" width="100%">
            <Paper elevation={0} sx={{ p: 3, mb: 4, border: '2px solid #000', borderRadius: '8px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Canais de Comercialização (em ordem de prioridade)
              </Typography>
              {canaisOrdenados.length > 0 ? (
                <List>
                  {canaisOrdenados.map((canal, index) => (
                    <div key={canal.value}>
                      <ListItem>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={2}>
                              <Chip
                                label={`${canal.order}º`}
                                color="primary"
                                variant="outlined"
                                size="small"
                              />
                              <Typography>
                                {canal.value === CuffaCommercializationChannelEnum.OUTRO
                                  ? `OUTRO: ${dados.outroTexto}`
                                  : canal.value}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < canaisOrdenados.length - 1 && <Divider />}
                    </div>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nenhum canal selecionado
                </Typography>
              )}
            </Paper>
            <Box marginTop="2rem" display="flex" justifyContent="center" gap="1rem">
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => navigate(ROUTES.SELOS_AGREGACAO_CUFFA_VISUALIZAR.replace(':uuid', uuid ?? ''))}
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  if (uuid) {
                    navigate(ROUTES.CREDITO_FINANCIAMENTO_CUFFA_VISUALIZAR.replace(':uuid', uuid));
                  }
                }}
              >
                Avançar
              </Button>
            </Box>
          </ContainerForm>
        </Box>
      </Box>
    </MenuProvider>
  );
};

export default CanaisComercializacaoVisualizar;
